import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "rag-llm",
  name: "RAG / LLM開発",
  nameEn: "RAG & LLM",
  seo: {
    title:
      "RAG / LLM開発を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "RAG（Retrieval-Augmented Generation）とLLMアプリ開発を実践的に学べるゆめさくエンジニア。LangChain・ベクトルDB・プロンプト設計まで、AIエンジニアに必要なスキルを習得しましょう。",
    url: "/columns/rag-llm-programming-school",
  },
  hero: {
    title: "RAG / LLM開発を学べる女性向けプログラミングスクール",
    englishTitle: "Learn RAG & LLM",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "RAG / LLM開発" },
  ],
  overview: {
    heading: "RAG / LLM開発とは？",
    paragraphs: [
      "RAG（Retrieval-Augmented Generation）は、大規模言語モデル（LLM）に外部知識を検索・参照させることで、より正確で最新の情報に基づいた回答を生成する技術です。社内ドキュメントや専門知識をAIに活用させるための中核的なアプローチとして注目されています。",
      "LLMアプリ開発では、OpenAI APIやClaude APIなどのLLMをバックエンドに組み込み、チャットボット、文書要約ツール、コード生成アシスタントなどのAIアプリケーションを構築します。LangChainやLlamaIndexなどのフレームワークを活用することで、複雑なAIワークフローも効率的に実装できます。",
      "ベクトルDB（Pinecone、Chroma、Weaviateなど）を使った類似検索、Embeddingによるテキストのベクトル化、プロンプトチェーン設計など、RAG/LLM開発に不可欠な技術スタックを体系的に学ぶことが重要です。",
    ],
    useCases: [
      {
        label: "社内ナレッジ検索AI",
        description:
          "社内ドキュメントやマニュアルをベクトルDBに格納し、自然言語で質問するだけで適切な回答を得られるRAGシステムを構築します。",
      },
      {
        label: "AIチャットボット開発",
        description:
          "LLMを活用したカスタマーサポートボットやFAQ自動応答システムなど、ビジネス向けのチャットAIを開発します。",
      },
      {
        label: "文書要約・レポート生成",
        description:
          "長文の契約書、論文、議事録などをLLMで自動要約し、重要ポイントを抽出するツールを構築します。",
      },
      {
        label: "AIエージェント開発",
        description:
          "LangChainのAgent機能を使い、複数のツールやAPIを自律的に呼び出して複雑なタスクを遂行するAIエージェントを構築します。",
      },
    ],
  },
  whyLearn: {
    heading: "RAG / LLM開発を学ぶべき3つの理由",
    items: [
      {
        title: "LLMエンジニアは最も需要が伸びている職種",
        description:
          "ChatGPTの登場以降、LLMを自社プロダクトに組み込めるエンジニアの需要が爆発的に増加しています。RAGを活用したAIアプリ開発スキルは、年収1000万円超のポジションへの近道です。",
      },
      {
        title: "RAGであらゆる業界のAI活用を実現できる",
        description:
          "RAGは「自社データ×LLM」を組み合わせる汎用的な技術のため、医療、法務、教育、金融などあらゆる業界でのAI導入プロジェクトに携わることができます。",
      },
      {
        title: "Pythonの基礎があれば始められる",
        description:
          "LangChainなどのフレームワークが複雑な処理を抽象化しているため、Pythonの基礎とAPI連携の知識があればRAG/LLMアプリ開発を始められます。機械学習の深い数学知識は必須ではありません。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでRAG / LLM開発を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、Phase 4の応用・上級カリキュラムとしてRAG/LLM開発を学びます。Pythonの基礎とAPI連携のスキルを身につけた上で、LLMアプリ開発の実践的なプロジェクトに取り組みます。",
      "単にAPIを叩くだけでなく、RAGのアーキテクチャ設計、ベクトルDBの選定・構築、プロンプトチェーン最適化まで、プロダクションレベルのLLMアプリを構築できるスキルを習得します。",
    ],
    topics: [
      "LLM API基礎（OpenAI API / Claude API）",
      "LangChain / LlamaIndex によるRAG構築",
      "Embeddingとベクトルデータベース（Chroma / Pinecone）",
      "プロンプトエンジニアリングとチェーン設計",
      "AIエージェントの設計と実装",
      "RAGの評価・精度改善手法",
      "実践プロジェクト：社内ナレッジRAGアプリ開発",
    ],
  },
  careerPaths: {
    heading: "RAG / LLM開発を活かせるキャリア",
    items: [
      {
        title: "AIエンジニア",
        description:
          "LLMを活用したプロダクト開発やRAGシステムの設計・構築を担当します。社内外のデータソースとLLMを連携させ、実用的なAIアプリケーションを実装する役割です。",
        salary: "600〜1,200万円",
      },
      {
        title: "LLMエンジニア",
        description:
          "LLMの選定・ファインチューニング・プロンプト最適化・RAGアーキテクチャ設計を専門とする新しい職種です。LLMアプリの品質と精度を追求するスペシャリストとして高い年収が期待できます。",
        salary: "700〜1,300万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜12ヶ月（ツールの変化が速く情報が古くなりがち）",
        luagate: "3〜4ヶ月（最新のベストプラクティスで効率的に学習）",
      },
      {
        label: "挫折率",
        selfStudy: "RAGの精度改善やデバッグで行き詰まりやすい",
        luagate: "実践的なプロジェクトで段階的にスキルアップ",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "チュートリアルのコピーで終わりがち",
        luagate: "実際のビジネス課題を想定したRAGアプリを開発",
      },
      {
        label: "質問対応",
        selfStudy: "RAGの精度が出ない原因の特定が困難",
        luagate: "LLM開発経験のあるメンターが設計からレビュー",
      },
      {
        label: "就職サポート",
        selfStudy: "LLM関連の求人情報が整理されていない",
        luagate: "AI企業とのネットワークを活かした就職支援",
      },
    ],
  },
  faq: [
    {
      question: "RAG / LLM開発を学ぶのにAI・機械学習の知識は必要ですか？",
      answer:
        "深い機械学習の知識は必須ではありません。Pythonの基礎とAPI連携の経験があれば始められます。ゆめさくエンジニアではLLMの仕組みを概念的に理解した上で、実践的なアプリ開発に集中するカリキュラムを提供しています。",
    },
    {
      question: "RAGとファインチューニングの違いは何ですか？",
      answer:
        "RAGはLLMに外部データを検索・参照させる手法で、モデル自体は変更しません。ファインチューニングはモデル自体を追加学習させる手法です。RAGの方がコストが低く、データの更新も容易なため、多くのユースケースでまずRAGが推奨されます。",
    },
    {
      question: "LLM APIの利用料金はどのくらいかかりますか？",
      answer:
        "学習段階ではAPIの利用量が少ないため、月数百円〜数千円程度で収まることがほとんどです。OpenAI APIやClaude APIには無料枠もあり、学習コストは最小限に抑えられます。",
    },
    {
      question: "RAG / LLM開発の将来性はどうですか？",
      answer:
        "企業のAI導入が加速する中、自社データを活用したLLMアプリのニーズは急拡大しています。RAGは「LLMを実務で使うための標準技術」として定着しつつあり、この分野のスキルを持つエンジニアの需要は今後も伸び続けると見込まれます。",
    },
  ],
  relatedLanguages: [
    { slug: "python", name: "Python" },
    { slug: "generative-ai", name: "生成AI" },
    { slug: "prompt-engineering", name: "プロンプトエンジニアリング" },
    { slug: "ai-ml", name: "AI・機械学習" },
  ],
};
